/**
 * App Integration Settings List Component
 *
 * A flexible table/card component for displaying integration data with interactive rows.
 * This component works exclusively with the uap-app-integration-settings-form component
 * and handles row-specific data submission and queue processing.
 *
 * Features:
 * - Table and card layouts with configurable columns
 * - Row loading states and queue processing
 * - Component loader integration for dynamic cell rendering
 * - Form submission for individual rows
 * - Automatic data updates from REST responses
 * - Queue processing for batch operations
 *
 * @example
 * ```html
 * <uap-app-integration-settings-list
 *     .columns=${[
 *         { key: 'name', header: 'Name' },
 *         { key: 'status', header: 'Status' },
 *         { key: 'actions', header: 'Actions' }
 *     ]}
 *     .data=${tableData}
 *     layout="table"
 *     show-headings>
 * </uap-app-integration-settings-list>
 * ```
 *
 * @since 6.9.0 Initial implementation
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _AutomatorAppIntegrationSettingsList_columns_accessor_storage, _AutomatorAppIntegrationSettingsList_data_accessor_storage, _AutomatorAppIntegrationSettingsList_showHeadings_accessor_storage, _AutomatorAppIntegrationSettingsList_layout_accessor_storage, _AutomatorAppIntegrationSettingsList_queue_accessor_storage, _AutomatorAppIntegrationSettingsList_loadingRows_accessor_storage;
// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
// Import utilities
import { QueueManager } from './utils/queue-manager';
import { listenForEvent, dispatchEvent } from '../../utils/event-manager';
// Import styles
import styles from './web-component-style.scss?inline';
/**
 * App Integration Settings List
 * @element uap-app-integration-settings-list
 */
let AutomatorAppIntegrationSettingsList = class AutomatorAppIntegrationSettingsList extends LitElement {
    constructor() {
        super(...arguments);
        _AutomatorAppIntegrationSettingsList_columns_accessor_storage.set(this, []);
        _AutomatorAppIntegrationSettingsList_data_accessor_storage.set(this, []);
        _AutomatorAppIntegrationSettingsList_showHeadings_accessor_storage.set(this, true);
        _AutomatorAppIntegrationSettingsList_layout_accessor_storage.set(this, 'table');
        _AutomatorAppIntegrationSettingsList_queue_accessor_storage.set(this, undefined);
        /**
         * Queue manager instance
         *
         * @type {QueueManager}
         */
        this.queueManager = new QueueManager(() => {
            dispatchEvent('queue-complete', { success: true }, false);
        }, (rowId, isLoading) => this.setRowLoading(rowId, isLoading));
        _AutomatorAppIntegrationSettingsList_loadingRows_accessor_storage.set(this, new Set());
    }
    /**
     * The columns to display in the settings list
     *
     * @type {SettingsTableColumn[]}
     */
    get columns() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsList_columns_accessor_storage, "f"); }
    set columns(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsList_columns_accessor_storage, value, "f"); }
    /**
     * The data to display in the settings list
     *
     * @type {TableRow[]}
     */
    get data() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsList_data_accessor_storage, "f"); }
    set data(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsList_data_accessor_storage, value, "f"); }
    /**
     * Whether to show the table headings
     *
     * @type {boolean}
     */
    get showHeadings() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsList_showHeadings_accessor_storage, "f"); }
    set showHeadings(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsList_showHeadings_accessor_storage, value, "f"); }
    /**
     * The layout of the settings list
     *
     * @type {string}
     */
    get layout() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsList_layout_accessor_storage, "f"); }
    set layout(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsList_layout_accessor_storage, value, "f"); }
    /**
     * Queue configuration for processing rows
     * If defined, the component will process all rows with queue: true
     *
     * @type {QueueConfig | undefined}
     */
    get queue() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsList_queue_accessor_storage, "f"); }
    set queue(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsList_queue_accessor_storage, value, "f"); }
    /**
     * Tracks which rows are currently loading
     *
     * @type {Set<string>}
     */
    get loadingRows() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsList_loadingRows_accessor_storage, "f"); }
    set loadingRows(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsList_loadingRows_accessor_storage, value, "f"); }
    /**
     * Called after the component's first update/render
     *
     * @return {Promise<void>}
     */
    async firstUpdated() {
        // Wait for the initial update to complete
        await this.updateComplete;
        // Listen for centralized events
        listenForEvent('data-update', this.handleDataUpdate.bind(this));
        listenForEvent('button-click', this.handleRowSubmission.bind(this));
        listenForEvent('submission-complete', this.handleSubmissionComplete.bind(this));
        // If queue is defined and we have queued rows, start processing
        if (this.queue && this.data.some(row => row.queue)) {
            // Use requestAnimationFrame to ensure we're not in the middle of an update cycle
            requestAnimationFrame(() => {
                this.startQueueProcessing();
            });
        }
    }
    /**
     * Handle data updates from REST responses
     *
     * @param {CustomEvent<IntegrationSettingsEvent>} event - The data update event
     *
     * @return {void}
     */
    handleDataUpdate(event) {
        // Type guard to ensure this is a data-update event
        if (event.detail.action !== 'data-update') {
            return;
        }
        const updateData = event.detail.data;
        const { data } = updateData;
        if (Array.isArray(data)) {
            // Update the component's data with the new data
            this.data = data;
            // Clear loading states for updated rows
            data.forEach(row => {
                if (row.id) {
                    this.setRowLoading(row.id, false);
                }
            });
        }
    }
    /**
     * Handle row submission events to manage loading states
     *
     * @param {CustomEvent<IntegrationSettingsEvent>} event - The row submission event
     *
     * @return {void}
     */
    handleRowSubmission(event) {
        this.handleRowLoadingEvent(event, true);
    }
    /**
     * Handle form submission completion events to clear loading states
     *
     * @param {CustomEvent<IntegrationSettingsEvent>} event - The submission complete event
     *
     * @return {void}
     */
    handleSubmissionComplete(event) {
        this.handleRowLoadingEvent(event, false);
    }
    /**
     * Handle row loading events - add or remove row from loading state
     *
     * @param {CustomEvent<IntegrationSettingsEvent>} event - The loading event
     * @param {boolean} add - Whether to add or remove the row from loading state
     *
     * @return {void}
     */
    handleRowLoadingEvent(event, add) {
        const { rowSubmission } = event.detail;
        if (!rowSubmission || !rowSubmission.rowId) {
            return;
        }
        if (add) {
            this.loadingRows.add(rowSubmission.rowId);
            this.loadingRows = new Set(this.loadingRows); // Trigger reactivity
            return;
        }
        this.loadingRows.delete(rowSubmission.rowId);
        this.loadingRows = new Set(this.loadingRows); // Trigger reactivity
    }
    /**
     * Set a row's loading state
     *
     * @param {string} rowId - The row ID to set loading state for
     * @param {boolean} isLoading - Whether the row is loading
     *
     * @return {void}
     */
    setRowLoading(rowId, isLoading) {
        if (isLoading) {
            this.loadingRows.add(rowId);
            this.loadingRows = new Set(this.loadingRows); // Trigger reactivity
            return;
        }
        this.loadingRows.delete(rowId);
        this.loadingRows = new Set(this.loadingRows); // Trigger reactivity
    }
    /**
     * Start processing the queue if configured
     *
     * @return {Promise<void>}
     */
    async startQueueProcessing() {
        const form = this.closest('form');
        if (!form || !this.queue) {
            console.warn('No form found or queue not configured');
            return;
        }
        // Start queue processing
        await this.queueManager.processQueue(form, this.data, this.queue, (updatedRows) => {
            this.data = updatedRows;
        }, this // Pass the component instance
        );
    }
    /**
     * Render all cells for a row
     *
     * @param {SettingsTableColumn[]} rowColumns - The columns for this row
     * @param {TableRow} row - The row data
     * @param {boolean} [isLoading=false] - Whether the row is loading
     *
     * @return {TemplateResult} The rendered cells
     */
    renderCells(rowColumns, row, isLoading = false) {
        return rowColumns.map(column => {
            // Determine cell type from the data for CSS classes
            const cellData = row.columns ? row.columns[column.key] : row[column.key];
            const cellType = this.getCellType(cellData, column.key);
            const cellClass = `settings-table__cell settings-table__cell--${cellType}`;
            const content = this.renderCellContent(column, row, isLoading);
            return html `<div class="${cellClass}">${content}</div>`;
        });
    }
    /**
     * Get the cell type for CSS classes based on the cell data
     *
     * @param {CellData} cellData - The cell data
     * @param {string} [columnKey] - The column key
     *
     * @return {string} The cell type string
     */
    getCellType(cellData, columnKey) {
        if (!cellData || !cellData.options || cellData.options.length === 0) {
            return 'text';
        }
        // Use the type from the first component
        return cellData.options[0].type;
    }
    /**
     * Render cell content based on its type using the component loader
     *
     * @param {SettingsTableColumn} column - The column configuration
     * @param {TableRow} row - The row data
     * @param {boolean} [isLoading=false] - Whether the row is loading
     *
     * @return {TemplateResult} The rendered cell content
     */
    renderCellContent(column, row, isLoading = false) {
        const cellData = row.columns ? row.columns[column.key] : row[column.key];
        const components = cellData?.options || [];
        // Add loading state to each component
        const componentsWithLoading = components.map((component) => ({
            ...component,
            loading: isLoading
        }));
        // Get layout from cell data, default to vertical
        const layout = cellData?.layout || 'vertical';
        return html `
			<uap-app-integration-settings-component-loader 
				.config=${{
            components: componentsWithLoading,
            layout: layout
        }}>
			</uap-app-integration-settings-component-loader>
		`;
    }
    /**
     * Render the description for a row
     *
     * @param {string} description - The description text
     * @param {boolean} isLoading - Whether the row is loading
     *
     * @return {TemplateResult} The rendered description
     */
    renderDescription(description, isLoading) {
        return html `
			<div class="settings-table__description">
				${isLoading
            ? html `<uo-placeholder height="14px"></uo-placeholder>`
            : html `
						<uap-app-integration-settings-component-loader 
							.config=${{
                components: [{ type: 'text', data: description }]
            }}>
						</uap-app-integration-settings-component-loader>
					`}
			</div>
		`;
    }
    /**
     * Render the settings table
     *
     * @return {TemplateResult} The rendered settings table
     */
    render() {
        const filteredData = this.data.filter(row => row && Object.keys(row).length > 0);
        const tableClass = `settings-table settings-table--${this.layout}`;
        const columnCount = this.columns.length;
        return html `
			<div class="${tableClass}" style="--uo-settings-table-columns: ${columnCount};">
				${this.showHeadings ? html `
					<div class="settings-table__row settings-table__headings">
						<div class="settings-table__content">
							${this.columns.map(column => html `
								<div class="settings-table__cell">
									${column.header || ''}
								</div>
							`)}
						</div>
					</div>
				` : ''}
				${filteredData.map(row => {
            const rowColumns = this.columns;
            const isLoading = this.loadingRows.has(row.id || '');
            return html `
						<div class="settings-table__row" data-row-id="${row.id || ''}">
							<div class="settings-table__content">
								${this.renderCells(rowColumns, row, isLoading)}
							</div>
							${row.description ? this.renderDescription(row.description, isLoading) : ''}
						</div>
					`;
        })}
			</div>
		`;
    }
};
_AutomatorAppIntegrationSettingsList_columns_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsList_data_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsList_showHeadings_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsList_layout_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsList_queue_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsList_loadingRows_accessor_storage = new WeakMap();
AutomatorAppIntegrationSettingsList.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: Array })
], AutomatorAppIntegrationSettingsList.prototype, "columns", null);
__decorate([
    property({
        type: Array,
        converter: {
            fromAttribute: (value) => {
                if (!value) {
                    return [];
                }
                try {
                    return JSON.parse(value);
                }
                catch {
                    console.error('Failed to parse settings table data');
                    return [];
                }
            }
        }
    })
], AutomatorAppIntegrationSettingsList.prototype, "data", null);
__decorate([
    property({
        type: Boolean,
        attribute: 'show-headings',
        converter: (value) => value === 'yes'
    })
], AutomatorAppIntegrationSettingsList.prototype, "showHeadings", null);
__decorate([
    property({ type: String })
], AutomatorAppIntegrationSettingsList.prototype, "layout", null);
__decorate([
    property({
        type: Object,
        converter: (value) => {
            if (!value) {
                return undefined;
            }
            try {
                const parsed = JSON.parse(value);
                return parsed === true ? {} : parsed;
            }
            catch {
                return value === 'true' ? {} : undefined;
            }
        }
    })
], AutomatorAppIntegrationSettingsList.prototype, "queue", null);
__decorate([
    state()
], AutomatorAppIntegrationSettingsList.prototype, "loadingRows", null);
AutomatorAppIntegrationSettingsList = __decorate([
    customElement('uap-app-integration-settings-list')
], AutomatorAppIntegrationSettingsList);
export { AutomatorAppIntegrationSettingsList };
